import React, { useEffect, useMemo } from 'react';
import confetti from 'canvas-confetti';

// Flow levels — gamified progression by completed experiences
export const FLOW_LEVELS = [
  { name: 'Paddleboard', emoji: '🏄', min: 1, color: '#0ea5e9' },
  { name: 'Kayak', emoji: '🛶', min: 3, color: '#10b981' },
  { name: 'Jet Ski', emoji: '🚤', min: 6, color: '#f59e0b' },
  { name: 'Pontoon', emoji: '🛥️', min: 10, color: '#8b5cf6' },
  { name: 'Yacht', emoji: '🛳️', min: 18, color: '#2563eb' },
  { name: 'Dolphin Rider', emoji: '🐬', min: 30, color: '#ec4899' },
];

export const getFlowLevel = (count) => {
  let idx = -1;
  FLOW_LEVELS.forEach((l, i) => { if (count >= l.min) idx = i; });
  return idx; // -1 = no level yet
};

export const FlowLevel = ({ completedCount = 0 }) => {
  const levelIdx = getFlowLevel(completedCount);
  const level = levelIdx >= 0 ? FLOW_LEVELS[levelIdx] : null;
  const next = FLOW_LEVELS[levelIdx + 1] || null;

  const progress = useMemo(() => {
    if (!next) return 100;
    const base = level ? level.min : 0;
    return Math.min(100, Math.round(((completedCount - base) / (next.min - base)) * 100));
  }, [completedCount, level, next]);

  // Confetti when a new level is reached (once per level)
  useEffect(() => {
    if (levelIdx < 0) return;
    let seen = -1;
    try { seen = parseInt(localStorage.getItem('bf_flow_level_seen') || '-1', 10); } catch (e) { /* private mode */ }
    if (levelIdx > seen) {
      try { localStorage.setItem('bf_flow_level_seen', String(levelIdx)); } catch (e) { /* noop */ }
      if (seen >= -1 && completedCount > 0) {
        setTimeout(() => {
          confetti({ particleCount: 90, spread: 75, origin: { y: 0.55 }, colors: ['#2563eb', '#10b981', '#f59e0b', '#ec4899'] });
        }, 600);
      }
    }
  }, [levelIdx, completedCount]);

  return (
    <div className="mt-6 pt-6 border-t border-gray-100" data-testid="flow-level-block">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center mb-3">Your Flow Level</p>

      {level ? (
        <div className="text-center">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl text-3xl mb-2 animate-in zoom-in duration-300"
            style={{ backgroundColor: `${level.color}14` }}
            data-testid="flow-level-badge"
          >
            {level.emoji}
          </div>
          <p className="text-base font-bold text-gray-900">{level.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{completedCount} experience{completedCount === 1 ? '' : 's'} completed</p>
        </div>
      ) : (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl text-3xl mb-2 bg-gray-50">🌊</div>
          <p className="text-sm font-semibold text-gray-700">Your journey starts with a splash</p>
          <p className="text-xs text-gray-500 mt-0.5">Complete your first experience to become a Paddleboarder</p>
        </div>
      )}

      {next && (
        <div className="mt-3">
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%`, backgroundColor: (level || next).color }}
              data-testid="flow-level-progress"
            />
          </div>
          <p className="text-[11px] text-gray-400 text-center mt-1.5">
            {next.min - completedCount} more to unlock {next.emoji} {next.name}
          </p>
        </div>
      )}

      {/* Level track */}
      <div className="flex justify-center gap-1.5 mt-3">
        {FLOW_LEVELS.map((l, i) => (
          <span
            key={l.name}
            title={`${l.name} — ${l.min}+ experiences`}
            className={`text-sm transition-all duration-300 ${i <= levelIdx ? 'opacity-100 scale-110' : 'opacity-25 grayscale'}`}
          >
            {l.emoji}
          </span>
        ))}
      </div>
    </div>
  );
};

export default FlowLevel;
